import axios from "axios";
import { getItemRestrictionErrors, isCommentAllowed, omitRestrictionError, RestrictionErrorAnswer } from "common";
import { produce } from "immer";
import { debounce, isEmpty, isEqual, mapValues, omit, omitBy, pickBy } from "lodash";
import {
    CatalogItemObject,
    CouponSource,
    FieldType,
    Form,
    Guest,
    GuestStatus,
    LineItemType,
    Merchandise,
    PaymentLineItemStatus,
    PaymentMethod,
    ProductObject,
    Purchase,
    PurchaseItemObject,
    PurchaseStatus,
    Seller,
} from "types";
import { getArrivalOffsetMinutes } from "../../../models/Purchase";
import { CheckoutFormValues } from "../CheckoutPurchaseScreen.types";
import { useCustomerDetailsStore } from "../store/customerDetailsStore";
import { usePurchaseResponseStore } from "../store/usePurchaseResponseStore";
import { RequestMethod } from "@/api/interceptors";
import { queryClient } from "@/api/queryClient";
import { generateQueryKey } from "@/hooks/helper";
import { refreshWareCatalogItemsCache } from "@/hooks/resources/useAdjustInventoryLot";
import { getProductFromCache } from "@/hooks/resources/useProduct";
import { hasPaymentLineItem } from "@/models/CreatePurchaseState";
import {
    getPrimaryGuest,
    hasAddonFieldsInGuest,
    updateGuestsWithPlaceholderNames,
    withQuestionnaireAddonOverrides,
} from "@/models/Guest";
import { getPurchasableMerchandiseTemplateIds } from "@/models/Merchandise";
import {
    AddonLineItem,
    CreatePurchaseItemState,
    CreatePurchaseLineItem,
    CreatePurchaseState,
    useCreatePurchaseStore,
    WareLineItem,
} from "@/stores/createPurchaseStore";
import { getSeller } from "@/utils/getSeller";

/** Every purchase save sends its limit errors here. A later rejection replaces catalogItemOpen only for its add-ons. */
export const captureAddOnRestrictionErrors = (error: unknown) => {
    const capturedErrors = getItemRestrictionErrors(error);
    if (isEmpty(capturedErrors)) {
        return;
    }

    const { addOnRestrictionErrors } = useCreatePurchaseStore.getState();
    const errorsByItemKey = { ...addOnRestrictionErrors, ...capturedErrors };
    const mergedErrors = mapValues(errorsByItemKey, (entry, itemKey) => {
        const catalogItemOpen = { ...addOnRestrictionErrors[itemKey]?.catalogItemOpen, ...entry.catalogItemOpen };
        return { ...entry, catalogItemOpen };
    });
    useCreatePurchaseStore.setState({ addOnRestrictionErrors: mergedErrors });
};

/**
 * Clears the limit messages before a save, since a change like the guest count can resolve one. A draft save does not
 * measure the per event limit, so catalogItemOpen stays, but only for a saved item: a position key can point at a
 * different item once one is removed, and is never read again once the item has an id.
 */
export const clearAddOnRestrictionErrors = () => {
    const { addOnRestrictionErrors, purchase } = useCreatePurchaseStore.getState();
    if (isEmpty(addOnRestrictionErrors)) {
        return;
    }

    const itemIds = new Set(purchase.items.map((item) => item.id));
    const savedItemEntries = pickBy(addOnRestrictionErrors, (entry, itemKey) => itemIds.has(itemKey));
    const keptEntries = mapValues(savedItemEntries, (entry) => {
        return { errors: {}, guests: {}, catalogItemOpen: entry.catalogItemOpen };
    });
    useCreatePurchaseStore.setState({
        addOnRestrictionErrors: omitBy(keptEntries, (entry) => isEmpty(entry.catalogItemOpen)),
    });
};

/**
 * @param itemKey The key the item's errors are stored under
 * @param answer The attendee's answer, whose error is cleared along with the add-on's booking error
 */
export const clearAddOnRestrictionError = (itemKey: string, templateId?: ID, answer?: RestrictionErrorAnswer) => {
    const entry = useCreatePurchaseStore.getState().addOnRestrictionErrors[itemKey];
    const hasAddOnError = Boolean(templateId && entry?.errors[templateId]);
    const hasAnswerError = Boolean(answer && entry?.guests[answer.guestIndex]?.[answer.fieldIndex]);
    if (!entry || (!hasAddOnError && !hasAnswerError)) {
        return;
    }

    useCreatePurchaseStore.setState((state) => ({
        addOnRestrictionErrors: {
            ...state.addOnRestrictionErrors,
            [itemKey]: omitRestrictionError(entry, templateId, answer),
        },
    }));
};

/** With "Ignore questionnaire for now" on, every questionnaire add-on answer is sent with the override. */
export const getGuestsPayload = (item: Pick<CreatePurchaseItemState, "guests" | "disableRequiredFormFields">) => {
    const guests = item.guests ?? [];
    return item.disableRequiredFormFields ? guests.map((guest) => withQuestionnaireAddonOverrides(guest)) : guests;
};

export const getAddonLineItemsPayload = (addonLineItems: AddonLineItem[] = []) => {
    return addonLineItems.map(({ overrideRestrictions, ...lineItem }) => {
        return {
            ...lineItem,
            ...(overrideRestrictions && { _overrideRestrictions: true }),
        };
    });
};

// Line item types owned by the dedicated store arrays (demographicLineItems, equipmentLineItems, etc.);
// stray copies in item.lineItems would be sent twice in the payload (XS-1738)
const catalogOwnedLineItemTypes = new Set<LineItemType>([
    LineItemType.DEMOGRAPHIC,
    LineItemType.DEMOGRAPHIC_SECONDARY,
    LineItemType.EQUIPMENT,
    LineItemType.ADDON,
    LineItemType.WARE,
]);

export const excludeCatalogOwnedLineItems = <T extends { type?: LineItemType }>(lineItems: T[]): T[] => {
    return lineItems.filter((lineItem) => !lineItem.type || !catalogOwnedLineItemTypes.has(lineItem.type));
};

export const getFilteredWareLineItems = (productId: string, wareLineItems: WareLineItem[]): WareLineItem[] => {
    const merchandise = getProductFromCache<Merchandise>(productId);
    if (!merchandise) {
        return wareLineItems.filter((lineItem) => lineItem.quantity > 0);
    }

    const allowedTemplateIds = getPurchasableMerchandiseTemplateIds(merchandise);
    return wareLineItems.filter((lineItem) => lineItem.quantity > 0 && allowedTemplateIds.has(lineItem.template.id));
};

export const hasZeroDiscountCoupon = (items: CreatePurchaseItemState[]) => {
    return items.some((item) => {
        return item.lineItems.some((lineItem) => {
            const isCouponDiscount = "type" in lineItem && lineItem.type === LineItemType.COUPON_DISCOUNT;
            const isGiftSource = "coupon" in lineItem && lineItem.coupon?.source === CouponSource.GIFT;

            return isCouponDiscount && !isGiftSource && lineItem.amount === 0;
        });
    });
};

export const hasCouponApplied = (items: CreatePurchaseItemState[]) => {
    return items.some((item) => {
        return item.lineItems.some((lineItem) => {
            return "type" in lineItem && lineItem.type === LineItemType.COUPON_DISCOUNT;
        });
    });
};

export const allowedKeysToMapPurchaseToStore: string[] = [
    "id",
    "template",
    "quantity",
    "object",
    "type",
    "code",
    "payment",
    "amount",
    "coupon",
    "catalog",
    "appliedOn",
    "unlockAmount",
    "price",
    "includeInAmount",
];

export const filterPurchaseItemsWithoutId = (items: CreatePurchaseItemState[]) => {
    return items.filter((item) => Boolean(item.id));
};

/**
 * Filters out all the {@link CreatePurchaseItemState items} without *id* from a {@link CreatePurchaseState purchase}
 *
 * Use Cases:
 * 1. For multi-item checkout, whenever user is selecting any unfinished product. i.e. item with scheduleType="time" and without arrivalTime, and without completing process trying to add a new product, need to remove unfinished items from purchase store
 *
 * @param {CreatePurchaseState} purchase
 */
export const getPurchaseItemsHavingId = (purchase: CreatePurchaseState): CreatePurchaseState => {
    return {
        ...purchase,
        items: filterPurchaseItemsWithoutId(purchase.items),
    };
};

export const getCouponAndAffiliateDepositLineItems = (purchase: CreatePurchaseState) => {
    return (
        purchase.lineItems?.filter((lineItem) => lineItem.coupon ?? lineItem.type === LineItemType.AFFILIATE_DEPOSIT) ??
        []
    );
};

const applyAutoCheckInToItems = (
    items: CreatePurchaseItemState[],
    firstItem: CreatePurchaseItemState | undefined,
    seller: Seller,
    autoCheckIn: boolean,
) => {
    if (!firstItem) {
        return items;
    }

    const autoCheckInOffset = seller.meta["xola/seller/quickCheckIn/autoCheckInOffset"];
    const arrivalOffset = getArrivalOffsetMinutes(firstItem.arrivalDate, firstItem.arrivalTime);

    if (autoCheckIn && autoCheckInOffset >= arrivalOffset) {
        return items.map((item) => ({
            ...item,
            guests: (item.guests ?? []).map((guest) => ({ ...guest, guestStatus: GuestStatus.ARRIVED })),
        }));
    }

    return items;
};

export const getPayload = (values: CheckoutFormValues) => {
    const seller = getSeller();
    const { purchase, purchaseId, dueNow, amount, autoCheckIn, paymentLineItem, organizer, isPurchaserOrganizer } =
        useCreatePurchaseStore.getState();

    const { customerEmail, customerName, phone } = useCustomerDetailsStore.getState();

    const isPartnerOnlyPurchase = purchase.items.every((item) => item.seller && item.seller.id !== seller.id);

    let items = purchase.items.map((item) => {
        const demographicLineItems = item.demographicLineItems ?? [];
        const equipmentLineItems = item.equipmentLineItems ?? [];
        const attendeeLineItems = (item.attendeeLineItems ?? []).filter((li) => li.quantity > 0);
        const addonLineItems = getAddonLineItemsPayload(item.addonLineItems);
        const wareLineItems = item.wareLineItems ?? [];
        const filteredWareLineItems =
            item.object === PurchaseItemObject.WARE
                ? getFilteredWareLineItems(item.product.id, wareLineItems)
                : wareLineItems;

        const itemLineItems = [
            ...demographicLineItems,
            ...equipmentLineItems,
            ...attendeeLineItems,
            ...addonLineItems,
            ...filteredWareLineItems,
            ...excludeCatalogOwnedLineItems(item.lineItems),
        ];

        const isCustomLineItemsAdded = item.lineItems.some((lineItem) => {
            return lineItem.type && [LineItemType.ADJUSTMENT, LineItemType.TAXED_ADJUSTMENT].includes(lineItem.type);
        });

        const isWareItem = item.object === PurchaseItemObject.WARE;

        const omitKeys = [
            "demographicLineItems",
            "equipmentLineItems",
            "attendeeLineItems",
            "addonLineItems",
            "wareLineItems",
            "customLineItems",
        ];
        if (isWareItem) {
            omitKeys.push("arrivalDate", "privacy", "guests", "guestStatus");
        }

        return {
            ...omit(item, omitKeys),
            ...(!isWareItem && {
                guests: sanitizeGuests(updateGuestsWithPlaceholderNames(getGuestsPayload(item), itemLineItems)),
            }),
            ...(item?.formResponse && { formResponse: item.formResponse }),
            ...(item?.fulfillmentStatus && { fulfillmentStatus: item.fulfillmentStatus }),
            lineItems:
                item.enableCustomLineItems && !isCustomLineItemsAdded
                    ? [...itemLineItems, ...(item.customLineItems ?? [])]
                    : itemLineItems,
        };
    }) as CreatePurchaseItemState[];

    items = applyAutoCheckInToItems(items, purchase.items[0], seller, autoCheckIn);

    items = items.map((item) => ({
        ...item,
        guests: item.guests?.map((guest) => ({
            ...guest,
            organizer: isPurchaserOrganizer && guest.primary,
        })),
    }));

    const paymentMethod = paymentLineItem.payment.method;
    const commentAllowed = isCommentAllowed(seller, paymentMethod);

    const methods = Object.keys(PaymentMethod) as Array<keyof typeof PaymentMethod>;
    const shouldHideCommentField = methods.some((key) => PaymentMethod[key] === paymentMethod) || !commentAllowed;

    const defaultPaymentLineItems =
        dueNow > 0
            ? [
                  {
                      ...paymentLineItem,
                      ...(purchase.payNow && { amount: -1 * Math.abs(purchase.payNow) }),
                      payment: {
                          ...paymentLineItem.payment,
                          ...(!shouldHideCommentField && values.comment && { comment: values.comment }),
                      },
                  },
              ]
            : [];

    let lineItems: CreatePurchaseLineItem[] = hasPaymentLineItem(paymentLineItem, purchase)
        ? defaultPaymentLineItems
        : [];
    if (purchase.lineItems) {
        const filteredPurchaseLineItems = getCouponAndAffiliateDepositLineItems(purchase);
        lineItems = [...lineItems, ...filteredPurchaseLineItems];
    }

    const payload: Partial<CreatePurchaseState> = {
        id: purchaseId!,
        lineItems,
        items,
        seller: { id: seller.id }, // Always use the logged-in (source) seller
        amount: amount,
        organizer: organizer && !isPartnerOnlyPurchase ? { id: organizer.id } : undefined,
        tags: values.tags ?? [],
        notes: values.note,
        splitPayment: purchase.splitPayment,
        paymentReminders: purchase.paymentReminders,
        currency: seller?.currency,
        deposit: values.deposit,
        status: PurchaseStatus.COMMITTED,
    };

    if (values.customerName || customerName) {
        payload.customerName = values.customerName || customerName;
    }

    if (values.customerEmail || customerEmail) {
        payload.customerEmail = values.customerEmail || customerEmail;
    }

    if (values.phone || phone) {
        payload.phone = values.phone || phone;
    }

    return payload;
};

export const getDetailsHeading = (object: ProductObject) => {
    switch (object) {
        case ProductObject.MEMBERSHIP:
            return "Membership";
        case ProductObject.PACKAGE:
            return "Package";
        default:
            return "Product";
    }
};

export const getPostCreateBookingAction = (seller: Seller, purchase: Purchase) => {
    const postBookingAction = seller.preferences?.booking?.postBookingAction;
    const itemCount = purchase.items?.length ?? 0;
    const itemId = itemCount === 1 ? `/${purchase.items[0].id}` : "";
    switch (postBookingAction) {
        case "goToDashboard":
            return `/dashboard`;
        case "createOrder":
            return `/cart/init`;
        default:
            return purchase.seller.id !== seller.id || itemCount === 0
                ? "/purchases"
                : `/purchases/${purchase.id}${itemId}`;
    }
};

const sanitizeGuests = (guests: Guest[]): Array<Omit<Guest, "checkInCount" | "ticketCode" | "id">> => {
    return guests.map((guest) => omit(guest, ["checkInCount", "ticketCode"]));
};

/** Turning it off clears every attendee's override, and either way the draft is saved again to check the limits. */
export const handleIgnoreQuestionnaireChange = (isIgnored: boolean) => {
    const { activeItemIndex, actions } = useCreatePurchaseStore.getState();

    actions.setPurchase((draft) => {
        const item = draft.items[activeItemIndex];
        item.disableRequiredFormFields = isIgnored;
        if (!isIgnored && item.guests) {
            item.guests = item.guests.map((guest) => withQuestionnaireAddonOverrides(guest, false));
        }
    });

    const { guests } = useCreatePurchaseStore.getState().purchase.items[activeItemIndex];
    if (guests?.some((guest) => hasAddonFieldsInGuest(guest))) {
        useCreatePurchaseStore.setState((state) => ({ rerunTrigger: state.rerunTrigger + 1 }));
    }
};

export const handleGuestListChange = (guests: Guest[]) => {
    const { activeItemIndex } = useCreatePurchaseStore.getState();
    const item = usePurchaseResponseStore.getState().purchase.items?.[activeItemIndex];
    const organizerOnly = item?.form?.organizerOnly ?? false;

    // Check if the guests have changed, if not, return early, not using lodash isEqual check as it does deep equality check and is expensive
    if (isEqual(item?.guests, guests)) {
        return;
    }

    const { setPurchase } = useCreatePurchaseStore.getState().actions;
    setPurchase((draft) => {
        const primaryGuest = getPrimaryGuest(guests);

        // If OrganizerOnly form then there will be only one guest and item level questionnaire
        if (organizerOnly && primaryGuest && primaryGuest.formResponse) {
            const { formResponse, name } = primaryGuest;
            delete primaryGuest.formResponse;
            draft.items[activeItemIndex].formResponse = {
                ...formResponse,
                fields: formResponse.fields,
                name: name,
            };
        }

        draft.items[activeItemIndex].guests = guests;
    });
};

export const handlePackageGuestListChange = (guests: Guest[]) => {
    let updatedGuests = guests;
    const organizerOnly = useCreatePurchaseStore.getState().getForm()?.organizerOnly ?? false;
    const { organizer } = useCreatePurchaseStore.getState();

    const { setPurchase } = useCreatePurchaseStore.getState().actions;

    setPurchase((draft) => {
        if (organizerOnly && guests?.[0].formResponse) {
            const { formResponse, name } = guests[0];
            updatedGuests = guests.map((guest) => omit(guest, "formResponse"));

            for (const item of draft.items) {
                item.formResponse = {
                    ...formResponse,
                    fields: formResponse.fields,
                    name: name,
                };
            }
        }

        for (const item of draft.items) {
            item.guests = updatedGuests;
        }

        if (organizer) {
            const purchaser = getPurchaserValues(draft, organizer.id);

            draft.customerName = purchaser.name;
            draft.customerEmail = purchaser.email;
            draft.phone = purchaser.phone;
        }
    });
};

/** Returns `name`, `email` and `phone` details for main guest. If `name` is not available it would return the placeholder name */
export const getPurchaserValues = (purchase: CreatePurchaseState, organizerId?: string) => {
    const purchaserItem = purchase.items.length > 1 ? findItemWithPurchaser(purchase) : purchase.items[0];
    const mainGuest = purchaserItem ? getMainGuest(purchaserItem, organizerId) : undefined;
    const form = useCreatePurchaseStore.getState().getForm();

    const formResponse = form?.organizerOnly ? purchaserItem?.formResponse : mainGuest?.formResponse;
    const email = formResponse?.fields.find((field) => field.type === FieldType.EMAIL)?.value ?? "";
    const phone = formResponse?.fields.find((field) => field.type === FieldType.PHONE)?.value ?? "";

    return { name: mainGuest?.name ?? "", email, phone, id: mainGuest?.id };
};

export const findItemWithPurchaser = (purchase: CreatePurchaseState) => {
    return purchase.items.find((item) => item.guests?.some((guest) => guest.primary));
};

export const isPurchaserEmailOrPhoneUpdated = (purchase: CreatePurchaseState) => {
    const { email, phone } = getPurchaserValues(purchase);
    const { customerEmail, phone: customerPhone } = useCustomerDetailsStore.getState();

    return (email && customerEmail !== email) || (phone && customerPhone !== phone);
};

export const getMainGuest = (purchaseItem: CreatePurchaseItemState, organizerId?: string): Guest | undefined => {
    const guests = updateGuestsWithPlaceholderNames(purchaseItem.guests ?? [], purchaseItem.lineItems);

    if (organizerId) {
        return guests.find((guest) => guest.id === organizerId);
    }

    /** We'll find a guest which has `primary` set to true, if no primary guests exists, we would consider the first guest as primary  */
    return guests.find((guest) => guest.primary) ?? guests[0];
};

export const isPurchaseNotFoundError = (error: any) => {
    return (
        error &&
        axios.isAxiosError(error) &&
        error?.config?.method === RequestMethod.PUT &&
        (error as any)?.response?.data?.error?.code === 404
    );
};

export const isGuestNotFoundError = (error: any) => {
    return (
        error &&
        axios.isAxiosError(error) &&
        error?.config?.method === RequestMethod.PUT &&
        JSON.stringify(error?.response?.data ?? "").includes("guest_not_found")
    );
};

/**
 * Creates new payload without purchase id, failed payment line items, and sub-total line items.
 */
export const resetPurchase = () => {
    useCreatePurchaseStore.setState(
        produce((draftStore) => {
            draftStore.purchaseId = null;

            if (draftStore.purchase.lineItems?.length) {
                draftStore.purchase.lineItems = draftStore.purchase.lineItems.filter(
                    (lineItem: CreatePurchaseLineItem) => {
                        return lineItem.status !== PaymentLineItemStatus.FAILED;
                    },
                );
            }

            draftStore.purchase.items = draftStore.purchase.items.map((item: CreatePurchaseItemState) => ({
                ...omit(item, ["guests", "id"]), // removing the item id as it can cause problems on deleting draft purchases in backend X2-9724.
            }));
        }),
    );
};

export const getPurchaseLineItems = (purchase: CreatePurchaseState) => {
    if (purchase.lineItems) {
        const filteredPurchaseLineItems = getCouponAndAffiliateDepositLineItems(purchase);
        return [...filteredPurchaseLineItems];
    }

    return [];
};

function getGuestsWithPrimaryField(guests: Guest[], fieldType: FieldType, fieldValue: string): Guest[] {
    return produce(guests, (draft) => {
        for (const guest of draft) {
            if (!guest.primary || !guest.formResponse) {
                continue;
            }

            const updatedFields = guest.formResponse.fields.map((field) =>
                field.type === fieldType ? { ...field, value: fieldValue } : field,
            );
            guest.formResponse = { fields: updatedFields };
        }
    });
}

export const syncGuestFormResponse = (fieldType: FieldType, fieldValue: string, form?: Form) => {
    const store = useCreatePurchaseStore.getState();
    if (!store.isPurchaserOrganizer) {
        return;
    }

    if (!form?.fields.some((field) => field.type === fieldType)) {
        return;
    }

    const { guests } = store.getActiveItem();

    if (!guests) {
        return;
    }

    const newGuests = getGuestsWithPrimaryField(guests, fieldType, fieldValue);
    handleGuestListChange(newGuests);
};

export const debouncedSyncGuestFormResponse = debounce(syncGuestFormResponse, 300);

const getOmittedData = (guests: Guest[]) => {
    return guests.map((guest) => ({
        ...omit(guest, "id", "name"),
        formResponse: omit(guest.formResponse, "id", "name"),
    }));
};

export const areGuestsEqual = (guestsA?: Guest[], guestsB?: Guest[]) => {
    if (!guestsA && !guestsB) {
        return true;
    }

    if (!guestsA || !guestsB) {
        return false;
    }

    if (guestsA.length !== guestsB.length) {
        return false;
    }

    return isEqual(getOmittedData(guestsA), getOmittedData(guestsB));
};

export const refetchMerchandiseCatalogItems = async (merchandiseId: string) => {
    // This utility runs outside React components/hooks, so it cannot use useInvalidate.
    try {
        const refreshedCatalogItems = await refreshWareCatalogItemsCache(merchandiseId);
        if (refreshedCatalogItems.length > 0) {
            return;
        }
    } catch {
        // Ignore refresh failures and rely on query invalidation fallback below.
    }

    await queryClient.invalidateQueries(
        generateQueryKey(["catalogItems", "collection", "infinite"], {
            product: merchandiseId,
            object: CatalogItemObject.WARE_CATALOG_ITEM,
        }),
        {
            refetchActive: true,
            refetchInactive: true,
        },
    );
};
